import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCcw, Send, Loader2, CheckCheck, AlertTriangle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import StatusBadge from '@/components/StatusBadge';

export default function CampaignDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [c, m] = await Promise.all([
        api.get(`/campaigns/${id}`),
        api.get(`/campaigns/${id}/messages`),
      ]);
      setCampaign(c.data);
      setMessages(m.data || []);
    } catch {
      toast.error('Failed to load campaign');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  // poll while sending
  useEffect(() => {
    if (campaign?.status !== 'SENDING') return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [campaign?.status]);

  const sendNow = async () => {
    setBusy(true);
    try {
      await api.post(`/campaigns/${id}/send-now`);
      toast.success('Sending started');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Send failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading || !campaign) {
    return <div className="glass-card p-10 text-center"><Loader2 className="h-5 w-5 mx-auto animate-spin" /></div>;
  }

  const filtered = messages.filter((m) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return m.contact_name?.toLowerCase().includes(s) || m.contact_phone?.toLowerCase().includes(s) || m.status?.toLowerCase().includes(s);
  });

  const counts = messages.reduce((acc, m) => {
    acc[m.status] = (acc[m.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate('/campaigns')} aria-label="Back" data-testid="campaign-detail-back"><ArrowLeft className="h-5 w-5" /></Button>
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight flex-1 truncate" data-testid="campaign-detail-title">{campaign.name}</h1>
        <StatusBadge status={campaign.status} kind="campaign" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <SummaryCard label="Total" value={campaign.total_count || messages.length} accent="bg-white/70" />
        <SummaryCard label="Sent" value={counts.sent || 0} accent="bg-[#FFF7ED] text-[#9A3412]" />
        <SummaryCard label="Delivered" value={counts.delivered || 0} accent="bg-[#EAF8F1] text-[#0F6B45]" />
        <SummaryCard label="Read" value={counts.read || 0} accent="bg-[#E6F6FF] text-[#075985]" />
        <SummaryCard label="Failed" value={counts.failed || 0} accent="bg-[#FEECEE] text-[#9F1239]" />
      </div>

      <div className="glass-card p-4 sm:p-5 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-2">
          <div className="text-xs uppercase text-muted-foreground">Template</div>
          <div className="font-mono text-sm">{campaign.templates?.name}</div>
          <div className="text-sm whitespace-pre-wrap mt-2">{campaign.templates?.body_text}</div>
          {campaign.media_id && (
            <div className="text-xs text-muted-foreground mt-2">Media: {campaign.media_type} · ID: {campaign.media_id?.slice(0, 14)}...</div>
          )}
        </div>
        <div className="space-y-2 text-sm">
          <div><span className="text-muted-foreground">Created:</span> <span>{new Date(campaign.created_at).toLocaleString()}</span></div>
          {campaign.scheduled_at && <div><span className="text-muted-foreground">Scheduled:</span> <span><Clock className="inline h-3.5 w-3.5 mr-1" />{new Date(campaign.scheduled_at).toLocaleString()}</span></div>}
          {campaign.started_at && <div><span className="text-muted-foreground">Started:</span> <span>{new Date(campaign.started_at).toLocaleString()}</span></div>}
          {campaign.completed_at && <div><span className="text-muted-foreground">Completed:</span> <span>{new Date(campaign.completed_at).toLocaleString()}</span></div>}
          <div className="pt-2 flex flex-wrap gap-2">
            <Button variant="outline" onClick={load} className="rounded-xl bg-white/60 border-white/60" data-testid="campaign-detail-refresh"><RefreshCcw className="h-4 w-4 mr-1.5" />Refresh</Button>
            {(campaign.status === 'DRAFT' || campaign.status === 'SCHEDULED') && (
              <Button onClick={sendNow} disabled={busy} className="gradient-orange text-white rounded-xl" data-testid="campaign-detail-send-now">
                {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}Send Now
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="glass-card overflow-hidden">
        <div className="p-3 border-b border-white/40">
          <Input placeholder="Search recipients by name/phone/status..." value={search} onChange={(e) => setSearch(e.target.value)} className="bg-white/70 border-white/60 rounded-xl max-w-md" data-testid="campaign-detail-search" />
        </div>
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm" data-testid="campaign-detail-recipients-table">
            <thead className="bg-white/40 border-b border-white/50">
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 hidden md:table-cell">Sent At</th>
                <th className="px-4 py-3 hidden lg:table-cell">Error</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">No messages yet</td></tr>
              ) : filtered.map((m) => (
                <tr key={m.id} className="border-b border-white/30 hover:bg-white/30 transition-colors">
                  <td className="px-4 py-3 font-medium">{m.contact_name}</td>
                  <td className="px-4 py-3 text-muted-foreground tabular-nums">{m.contact_phone}</td>
                  <td className="px-4 py-3"><StatusBadge status={m.status} kind="message" /></td>
                  <td className="px-4 py-3 hidden md:table-cell text-xs">{m.sent_at ? new Date(m.sent_at).toLocaleString() : '—'}</td>
                  <td className="px-4 py-3 hidden lg:table-cell text-xs text-[#9F1239] max-w-xs truncate">{m.error_message || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, accent }) {
  return (
    <div className={`glass-card p-3 sm:p-4 ${accent || ''}`}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-2xl font-bold tabular-nums mt-0.5">{value}</div>
    </div>
  );
}
